import { useOutletContext, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Grid from '../../components/grid/Grid';
import { getUploadedFilesForDevice, downloadSelectedFiles } from '../../services/UploadedFilesAPI';
import { formatToBrowserTime, formatToUtcIso } from '../../utils/timeFormat';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DateRange from '../../components/dateRange/DateRange';
import { faSyncAlt, faSearch, faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { DEFAULT_DAYS_UPLOADEDFILES_DATA, MAX_DOWNLOAD_BYTES } from '../../utils/constants';
import dayjs from 'dayjs';
import './UploadedFiles.css'

const DEFAULT_RANGE_DAYS = DEFAULT_DAYS_UPLOADEDFILES_DATA;

const UploadedFiles = () => {
    const { t } = useTranslation();
    const location = useLocation();

    const ctx = useOutletContext?.() || {};
    const qs = new URLSearchParams(location.search);
    const model = ctx?.model ?? qs.get('model') ?? '';
    const serialNumber = ctx?.serialNumber ?? qs.get('serialNumber') ?? '';

    const nowFloored = dayjs();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [startDate, setStartDate] = useState(() =>
        nowFloored.subtract(DEFAULT_RANGE_DAYS, 'day')
    );
    const [endDate, setEndDate] = useState(() => nowFloored);
    const [rangeError, setRangeError] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDownloadDisabledBySize, setIsDownloadDisabledBySize] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const headers = [
        { key: 'id', label: 'ID', visible: false },
        { key: 'PK', label: 'Asset Name', visible: false },
        { key: 'name', label: 'Name', width: '25%' },
        { key: 'path', label: 'Path', width: '40%' },
        { key: 'size', label: 'Size(Bytes)', width: '15%', alignHeader: 'center', alignContent: 'center' },
        { key: 'SK', label: 'Uploaded Date', width: '20%' },
    ];

    const buildSearchData = (s = startDate, e = endDate) => {
        const now = dayjs();
        let start = dayjs(s);
        let end = dayjs(e);

        if (end.isAfter(now)) end = now;

        return {
            device_name: `${model}-${serialNumber}`,
            start_date: formatToUtcIso(start.toDate(), false),
            end_date: formatToUtcIso(end.toDate(), false),
        };
    };

    const mapApiResponseToGridData = (dataArray) => {
        return (dataArray ?? []).map((item, index) => ({
            id: index + 1,
            PK: item?.PK ?? '',
            name: item?.name ?? '',
            path: item?.path ?? '',
            size: item?.size ?? '',
            SK: item?.SK
                ? formatToBrowserTime(item.SK, false, false) // 24-hour with millis
                : ''
        }));
    };

    const loadData = async (searchData) => {
        try {
            setLoading(true);
            const raw = await getUploadedFilesForDevice(searchData);  // Fetch real data from signed AWS request
            const formatted = Array.isArray(raw) ? raw : [raw]; // Ensure it's an array
            const mapped = mapApiResponseToGridData(formatted);
            setData(mapped);
        } catch (err) {
            console.error('Fetch failed:', err);
        } finally {
            setLoading(false);
        }
    };

    const downloadFiles = async (filesPayload) => {
        setDownloadLoading(true);
        try {
            const res = await downloadSelectedFiles(filesPayload);
            const url = res?.presigned_url;
            if (!url) throw new Error('No presigned URL returned from server.');
            const filename = res?.zip_key ? `${res.zip_key}.zip` : decodeURIComponent(new URL(url).pathname.split('/').pop() || 'download');
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.rel = 'noreferrer';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Download started');
        } catch (err) {
            console.error('Download error:', err);
            toast.error('Failed to start download');
        } finally {
            setDownloadLoading(false);
        }
    };

    useEffect(() => {
        if (!model || !serialNumber) {
            setLoading(false);
            toast.error('Missing model or serial number.');
            return;
        }

        loadData(buildSearchData());
    }, [model, serialNumber]);

    const handleSearch = () => {
        if (rangeError) return;
        loadData(buildSearchData(startDate, endDate));
    };

    const handleReset = () => {
        const now = dayjs();
        const start = now.subtract(DEFAULT_RANGE_DAYS, 'day');
        setStartDate(start);
        setEndDate(now);
        setRangeError('');
        loadData(buildSearchData(start, now));
    };
    const parseSizeToNumber = (sizeValue) => {
        if (sizeValue == null) return 0;
        if (typeof sizeValue === 'string') {
            const cleaned = sizeValue.replace(/,/g, '').trim();
            const n = Number(cleaned);
            return Number.isFinite(n) ? n : 0;
        }
        if (typeof sizeValue === 'number') return sizeValue;
        return 0;
    };

    useEffect(() => {
        const total = (selectedRows || []).reduce((acc, row) => {
            return acc + parseSizeToNumber(row?.size);
        }, 0);
        setIsDownloadDisabledBySize(total > MAX_DOWNLOAD_BYTES);
    }, [selectedRows]);

    const handleDownload = () => {
        if (isDownloadDisabledBySize) return;
        if (!selectedRows || selectedRows.length === 0) return;
        const payload = {
            s3_keys: selectedRows.map(row => row.path)
        };

        downloadFiles(payload);
    };

    const noFilesSelectedTitle = selectedRows.length === 0 ? 'No files selected' : undefined


    return (
        <div className='uf-page-wrapper'>
            <div className="uf-header ">
                <div
                    className='left-align'
                    title={
                        isDownloadDisabledBySize
                            ? 'Selected files exceed 1 GB limit'
                            : (rangeError || noFilesSelectedTitle)
                    }
                >
                    <button
                        className="btn btn-download"
                        onClick={handleDownload}
                        disabled={loading || !!rangeError || isDownloadDisabledBySize || selectedRows.length === 0 || downloadLoading}
                    >
                        <FontAwesomeIcon icon={faDownload} className="me-1" />
                        {t(`Download(${selectedRows.length})`) || `Download(${selectedRows.length})`}
                        {downloadLoading && <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />}
                    </button>
                </div>
                <DateRange
                    defaultDays={DEFAULT_RANGE_DAYS}
                    startTime={startDate}
                    endTime={endDate}
                    onChange={({ startDate: s, endDate: e, rangeError: err }) => {
                        setStartDate(s);
                        setEndDate(e);
                        setRangeError(err);
                    }}
                />
                <button
                    className="btn btn-primary"
                    onClick={handleSearch}
                    disabled={loading || !!rangeError}
                    title={rangeError || undefined}
                >
                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                    {t('Search') || 'Search'}
                </button>
                <button className="btn btn-primary" onClick={handleReset}>
                    <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                    Refresh
                </button>
            </div>
            <div className='uf-content'>
                <Grid
                    headers={headers}
                    data={data}
                    rowsPerPage={20}
                    canMultiSelect={true}
                    onSelectionChange={setSelectedRows}
                    loading={loading}
                    sortable={true}
                />
            </div>
        </div>
    );
};

export default UploadedFiles;